'use client';

import { useState, type ReactNode } from 'react';
import { EyeOff } from 'lucide-react';

interface ProfilePhotoImgProps {
	url?: string | null;
	alt: string;
	/** true si la foto está oculta (privada): se muestra opacada + badge, sólo si el visor puede cargarla. */
	oculta?: boolean;
	imgClassName?: string;
	/** Qué mostrar si no hay foto o si el servidor la deniega (no-dueño de una foto oculta). */
	fallback: ReactNode;
	badgeSize?: number;
}

/**
 * Muestra la foto de perfil. Si la URL falla (p. ej. el AssetStore deniega una foto oculta
 * a quien no es dueño/admin), cae al `fallback`. Para fotos ocultas que sí se pueden cargar
 * (dueño/admin) se muestran opacadas con un badge de "oculta".
 */
export function ProfilePhotoImg({ url, alt, oculta, imgClassName, fallback, badgeSize = 12 }: ProfilePhotoImgProps) {
	const [failed, setFailed] = useState(false);

	if (!url || failed) return <>{fallback}</>;

	return (
		<>
			{/* eslint-disable-next-line @next/next/no-img-element */}
			<img
				src={url}
				alt={alt}
				onError={() => setFailed(true)}
				className={imgClassName}
				style={oculta ? { opacity: 0.5 } : undefined}
			/>
			{oculta && (
				<span
					title="Foto oculta: sólo tú y los administradores la ven"
					style={{
						position: 'absolute',
						bottom: 4,
						right: 4,
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: badgeSize + 9,
						height: badgeSize + 9,
						borderRadius: 999,
						background: 'rgba(15, 23, 42, 0.7)',
						color: '#fff',
					}}
				>
					<EyeOff size={badgeSize} />
				</span>
			)}
		</>
	);
}
